import * as React from "react";
import { useState, useRef, useEffect, useMemo } from "react";
import compact from "lodash/compact";
import camelCase from "lodash/camelCase";
import mapKeys from "lodash/mapKeys";
import mapValues from "lodash/mapValues";
import { useTranslation } from "react-i18next";
import i18n from "i18n";
import { validateField } from "util/FormsUtils";
import { getEnterpriseGroupSyncPlugin } from "util/AuthenticationService";
import { Row, Col, Alert } from "components/bootstrap";
import { Spinner } from "components/common";
import AuthzRolesDomain from "domainActions/roles/AuthzRolesDomain";
import Routes from "routing/Routes";
import Wizard from "components/common/Wizard";
import useHistory from "routing/useHistory";
import BackendWizardContext from "./BackendWizardContext";
import { FORM_VALIDATION as SERVER_CONFIG_VALIDATION, STEP_KEY as SERVER_CONFIG_KEY } from "./ServerConfigStep";
import { FORM_VALIDATION as USER_SYNC_VALIDATION, STEP_KEY as USER_SYNC_KEY } from "./UserSyncStep";
import { STEP_KEY as GROUP_SYNC_KEY } from "./GroupSyncStep";
import wizardSteps from "./wizardSteps";
import Sidebar from "./Sidebar";
const FORMS_VALIDATION = {
  [SERVER_CONFIG_KEY]: SERVER_CONFIG_VALIDATION,
  [USER_SYNC_KEY]: USER_SYNC_VALIDATION
};
const SubmitAllError = ({ error, backendId }) => {
  var _a, _b;
  const { t } = useTranslation("authentication");
  return /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { xs: 9, xsOffset: 3 }, /* @__PURE__ */ React.createElement(
    Alert,
    {
      bsStyle: "danger",
      style: { wordBreak: "break-word" },
      title: backendId ? t("wizard.edit_failed") : t("wizard.create_failed")
    },
    (error == null ? void 0 : error.message) && /* @__PURE__ */ React.createElement(React.Fragment, null, error.message, /* @__PURE__ */ React.createElement("br", null), /* @__PURE__ */ React.createElement("br", null)),
    (_b = (_a = error == null ? void 0 : error.additional) == null ? void 0 : _a.res) == null ? void 0 : _b.text
  )));
};
const _formatBackendValidationErrors = (backendErrors) => {
  const backendErrorStrings = mapValues(
    backendErrors,
    (errorArray) => i18n.t("wizard.server_validation_error", { ns: "authentication", errors: errorArray.join(" ") })
  );
  return mapKeys(backendErrorStrings, (_value, key) => camelCase(key));
};
export const _passwordPayload = (backendId, systemUserPassword) => {
  const _formatPayload = (password) => {
    if (!password) {
      return void 0;
    }
    return password;
  };
  if (backendId) {
    if (systemUserPassword === void 0) {
      return { keep_value: true };
    }
    if (systemUserPassword === "") {
      return { delete_value: true };
    }
    return { set_value: _formatPayload(systemUserPassword) };
  }
  return _formatPayload(systemUserPassword);
};
const _prepareSubmitPayload = (stepsState, getUpdatedFormsValues) => (overrideFormValues) => {
  const formValues = overrideFormValues != null ? overrideFormValues : getUpdatedFormsValues();
  const {
    defaultRoles = "",
    defaultUserTimezone,
    description,
    serverHost,
    serverPort,
    systemUserDn,
    systemUserPassword,
    title,
    transportSecurity,
    userUniqueIdAttribute,
    userFullNameAttribute,
    userNameAttribute,
    emailAttributes,
    userSearchBase,
    userSearchPattern,
    verifyCertificates
  } = formValues;
  const { serviceType, backendId } = stepsState.authBackendMeta;
  return {
    title,
    description,
    default_roles: defaultRoles.split(","),
    default_user_timezone: defaultUserTimezone,
    config: {
      servers: [{ host: serverHost, port: serverPort }],
      system_user_dn: systemUserDn,
      system_user_password: _passwordPayload(backendId, systemUserPassword),
      transport_security: transportSecurity,
      type: serviceType,
      email_attributes: emailAttributes,
      user_full_name_attribute: userFullNameAttribute,
      user_name_attribute: userNameAttribute,
      user_search_base: userSearchBase,
      user_search_pattern: userSearchPattern,
      user_unique_id_attribute: userUniqueIdAttribute,
      verify_certificates: verifyCertificates
    }
  };
};
const _getInvalidStepKeys = (formValues, newBackendValidationErrors, excludedFields) => {
  const validation = { ...FORMS_VALIDATION, [GROUP_SYNC_KEY]: {} };
  const enterpriseGroupSyncPlugin = getEnterpriseGroupSyncPlugin();
  const groupSyncValidation = enterpriseGroupSyncPlugin == null ? void 0 : enterpriseGroupSyncPlugin.validation.GroupSyncValidation;
  if (groupSyncValidation && formValues.synchronizeGroups) {
    validation[GROUP_SYNC_KEY] = groupSyncValidation(formValues.teamSelectionType);
  }
  const invalidStepKeys = Object.entries(validation).map(([stepKey, formValidation]) => {
    const stepHasError = Object.entries(formValidation).some(([fieldName, fieldValidation]) => {
      if (excludedFields[fieldName]) {
        return false;
      }
      if (newBackendValidationErrors == null ? void 0 : newBackendValidationErrors[fieldName]) {
        return true;
      }
      return !!validateField(fieldValidation)(formValues == null ? void 0 : formValues[fieldName]);
    });
    return stepHasError ? stepKey : void 0;
  });
  return compact(invalidStepKeys);
};
const _onSubmitAll = (stepsState, setSubmitAllError, onSubmit, getUpdatedFormsValues, getSubmitPayload, validateSteps, shouldUpdateGroupSync, history) => {
  const formValues = getUpdatedFormsValues();
  const invalidStepKeys = validateSteps(formValues, {});
  if (invalidStepKeys.length >= 1) {
    return Promise.resolve();
  }
  setSubmitAllError(null);
  const payload = getSubmitPayload(formValues);
  const _submit = () => onSubmit(payload, formValues, stepsState.authBackendMeta.serviceType, shouldUpdateGroupSync).then(() => {
    history.push(Routes.SYSTEM.AUTHENTICATION.BACKENDS.OVERVIEW);
  }).catch((error) => {
    var _a, _b;
    if (typeof ((_b = (_a = error == null ? void 0 : error.additional) == null ? void 0 : _a.body) == null ? void 0 : _b.errors) === "object") {
      const backendValidationErrors = _formatBackendValidationErrors(error.additional.body.errors);
      validateSteps(formValues, backendValidationErrors);
    } else {
      setSubmitAllError(error);
    }
  });
  if (stepsState.authBackendMeta.backendGroupSyncIsActive && !formValues.synchronizeGroups) {
    if (
      // eslint-disable-next-line no-alert
      window.confirm(i18n.t("wizard.confirm_remove_group_sync", { ns: "authentication" }))
    ) {
      return _submit();
    }
    return Promise.resolve();
  }
  return _submit();
};
const _setDefaultCreateRole = (roles, stepsState, setStepsState) => {
  var _a;
  const defaultCreateRoleId = (_a = roles == null ? void 0 : roles.find((role) => role.name === "Reader")) == null ? void 0 : _a.id;
  if (defaultCreateRoleId) {
    setStepsState({ ...stepsState, formValues: { ...stepsState.formValues, defaultRoles: defaultCreateRoleId } });
  }
};
const _loadRoles = (setPaginatedRoles) => {
  const getUnlimited = { page: 1, perPage: 0, query: "" };
  AuthzRolesDomain.loadRolesPaginated(getUnlimited).then(setPaginatedRoles);
};
const BackendWizard = ({
  initialValues,
  initialStepKey = SERVER_CONFIG_KEY,
  onSubmit,
  authBackendMeta,
  help = void 0,
  excludedFields = {}
}) => {
  const enterpriseGroupSyncPlugin = getEnterpriseGroupSyncPlugin();
  const MatchingGroupsProvider = enterpriseGroupSyncPlugin == null ? void 0 : enterpriseGroupSyncPlugin.components.MatchingGroupsProvider;
  const [paginatedRoles, setPaginatedRoles] = useState();
  const [submitAllError, setSubmitAllError] = useState();
  const [stepsState, setStepsState] = useState({
    activeStepKey: initialStepKey,
    authBackendMeta,
    backendValidationErrors: void 0,
    formValues: initialValues,
    invalidStepKeys: []
  });
  const history = useHistory();
  const formRefs = {
    [SERVER_CONFIG_KEY]: useRef(null),
    [USER_SYNC_KEY]: useRef(null),
    [GROUP_SYNC_KEY]: useRef(null)
  };
  const wizardContextValue = useMemo(() => ({ ...stepsState, setStepsState }), [stepsState, setStepsState]);
  useEffect(() => _loadRoles(setPaginatedRoles), []);
  useEffect(() => {
    if (paginatedRoles && !authBackendMeta.backendId && !stepsState.formValues.defaultRoles) {
      _setDefaultCreateRole(paginatedRoles.list, stepsState, setStepsState);
    }
  }, [paginatedRoles, authBackendMeta.backendId, stepsState, setStepsState]);
  if (!paginatedRoles) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  const _getUpdatedFormsValues = () => {
    var _a;
    const activeForm = (_a = formRefs[stepsState.activeStepKey]) == null ? void 0 : _a.current;
    return { ...stepsState.formValues, ...activeForm == null ? void 0 : activeForm.values };
  };
  const _validateSteps = (formValues, newBackendValidationErrors) => {
    const invalidStepKeys = _getInvalidStepKeys(formValues, newBackendValidationErrors, excludedFields);
    if (invalidStepKeys.length >= 1) {
      const nextStepKey = invalidStepKeys.includes(stepsState.activeStepKey) ? stepsState.activeStepKey : invalidStepKeys[0];
      setStepsState({
        ...stepsState,
        backendValidationErrors: newBackendValidationErrors,
        activeStepKey: nextStepKey,
        formValues,
        invalidStepKeys
      });
    }
    return invalidStepKeys;
  };
  const _getSubmitPayload = _prepareSubmitPayload(stepsState, _getUpdatedFormsValues);
  const _setActiveStepKey = (stepKey) => {
    const formValues = _getUpdatedFormsValues();
    let invalidStepKeys = [...stepsState.invalidStepKeys];
    if (invalidStepKeys.length >= 1) {
      invalidStepKeys = _getInvalidStepKeys(formValues, stepsState.backendValidationErrors, excludedFields);
    }
    setStepsState({
      ...stepsState,
      invalidStepKeys,
      formValues,
      activeStepKey: stepKey
    });
  };
  const _handleSubmitAll = (shouldUpdateGroupSync) => _onSubmitAll(
    stepsState,
    setSubmitAllError,
    onSubmit,
    _getUpdatedFormsValues,
    _getSubmitPayload,
    _validateSteps,
    shouldUpdateGroupSync,
    history
  );
  const steps = wizardSteps({
    formRefs,
    help,
    handleSubmitAll: _handleSubmitAll,
    invalidStepKeys: stepsState.invalidStepKeys,
    prepareSubmitPayload: _getSubmitPayload,
    excludedFields,
    roles: paginatedRoles.list,
    setActiveStepKey: _setActiveStepKey,
    submitAllError: submitAllError && /* @__PURE__ */ React.createElement(SubmitAllError, { error: submitAllError, backendId: authBackendMeta.backendId })
  });
  const wizard = /* @__PURE__ */ React.createElement(
    Wizard,
    {
      activeStep: stepsState.activeStepKey,
      hidePreviousNextButtons: true,
      horizontal: true,
      justified: true,
      onStepChange: _setActiveStepKey,
      steps
    },
    /* @__PURE__ */ React.createElement(Sidebar, { prepareSubmitPayload: _getSubmitPayload })
  );
  return /* @__PURE__ */ React.createElement(BackendWizardContext.Provider, { value: wizardContextValue }, MatchingGroupsProvider ? /* @__PURE__ */ React.createElement(MatchingGroupsProvider, { prepareSubmitPayload: _getSubmitPayload }, wizard) : wizard);
};
export default BackendWizard;
